import React, { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { adminApi } from "../../../lib/api";
import NotConnected from "./NotConnected";

const PERIODS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

const Trend = ({ d }) => {
  if (!d || d.pct === null || d.pct === undefined) {
    return <span className="text-[11px] text-slate-400">no comparison</span>;
  }
  const Icon = d.direction === "up" ? ArrowUpRight : d.direction === "down" ? ArrowDownRight : Minus;
  const tone = d.direction === "up" ? "text-emerald-600" : d.direction === "down" ? "text-red-500" : "text-slate-400";
  return (
    <span className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${tone}`}>
      <Icon size={12} /> {Math.abs(d.pct)}%
    </span>
  );
};

const Metric = ({ label, metric, value, suffix = "", hint, onOpen, testid }) => (
  <button
    type="button"
    onClick={onOpen}
    disabled={!onOpen}
    data-testid={testid}
    className={`text-left bg-white rounded-xl border border-slate-200 p-4 w-full duration-200 [transition-property:border-color,transform] ${
      onOpen ? "hover:border-orange hover:-translate-y-0.5" : "cursor-default"
    }`}
  >
    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    <p className="text-2xl font-extrabold text-navy mt-1 tabular-nums">
      {value !== undefined ? value : metric ? Math.round(metric.value).toLocaleString() : "—"}
      {suffix}
    </p>
    <div className="mt-1 flex items-center gap-2">
      <Trend d={metric} />
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </div>
  </button>
);

const Block = ({ title, source, note, children }) => (
  <section className="space-y-3">
    <div>
      <h3 className="font-display font-bold text-navy">{title}</h3>
      <p className="text-xs text-slate-400">
        {source}
        {note ? ` · ${note}` : ""}
      </p>
    </div>
    {children}
  </section>
);

export default function OverviewTab({ onOpenTab }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    adminApi.reporting
      .overview(days)
      .then((d) => live && setData(d))
      .catch(() => live && setData(null))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [days]);

  if (loading && !data) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!data) return <p className="text-sm text-red-500">The reporting roll-up could not be loaded.</p>;

  const leads = data.leads?.metrics || {};
  const search = data.search || {};

  return (
    <div className="space-y-8" data-testid="google-overview">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.days}
            type="button"
            onClick={() => setDays(p.days)}
            data-testid={`google-period-${p.days}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold duration-200 [transition-property:background-color,color] ${
              days === p.days ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {p.label}
          </button>
        ))}
        <span className="text-[11px] text-slate-400 ml-1">
          against the previous {days} days · each source keeps its own measurement
        </span>
        <button
          type="button"
          onClick={() => onOpenTab("integrations")}
          data-testid="google-overview-connections"
          className="ml-auto text-[11px] font-bold text-navy hover:text-orange duration-200 [transition-property:color]"
        >
          Connections & setup →
        </button>
      </div>

      <Block title="Enquiries and opportunities" source="First-party records — the enquiries themselves">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Metric label="Total enquiries" metric={leads.total_enquiries} onOpen={() => onOpenTab("leads")} testid="metric-total-enquiries" />
          <Metric label="Pricing enquiries" metric={leads.pricing_enquiries} onOpen={() => onOpenTab("leads")} testid="metric-pricing-enquiries" />
          <Metric label="Confirmed Discovery Calls" metric={leads.bookings_confirmed} onOpen={() => onOpenTab("leads")} testid="metric-bookings" />
          <Metric label="Journeys started" metric={leads.journeys_started} onOpen={() => onOpenTab("leads")} testid="metric-journeys" />
          <Metric label="Reached a price" metric={leads.quotes_generated} onOpen={() => onOpenTab("leads")} testid="metric-quotes" />
          <Metric
            label="Price to enquiry"
            value={leads.quote_to_enquiry_rate?.value == null ? "—" : `${leads.quote_to_enquiry_rate.value}%`}
            hint="of journeys that saw a price"
            onOpen={() => onOpenTab("leads")}
            testid="metric-conversion"
          />
          <Metric label="Guide downloads" metric={leads.guide_downloads} testid="metric-guides" />
          <Metric label="Booking page opened" metric={leads.booking_intent} hint="not yet confirmed" testid="metric-booking-intent" />
        </div>
      </Block>

      <Block
        title="Organic search"
        source={search.connected ? `Google Search Console · ${search.property}` : "Google Search Console"}
        note={search.connected ? search.note : ""}
      >
        {search.connected ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Metric label="Clicks" metric={search.metrics?.clicks} onOpen={() => onOpenTab("search")} testid="metric-gsc-clicks" />
              <Metric label="Impressions" metric={search.metrics?.impressions} onOpen={() => onOpenTab("search")} testid="metric-gsc-impressions" />
              <Metric label="Click-through rate" value={`${search.metrics?.ctr?.value ?? 0}%`} metric={search.metrics?.ctr} onOpen={() => onOpenTab("search")} testid="metric-gsc-ctr" />
              <Metric label="Average position" value={search.metrics?.position?.value?.toFixed?.(1) ?? "—"} metric={search.metrics?.position} onOpen={() => onOpenTab("search")} testid="metric-gsc-position" />
            </div>
            {(search.series || []).length > 1 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 h-64" data-testid="google-search-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={search.series} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="key" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                    {/* Clicks run in single figures against impressions in the
                        hundreds, so they need their own axis or the line sits flat on zero. */}
                    <YAxis yAxisId="clicks" orientation="left" tick={{ fontSize: 11, fill: "#f97316" }} tickLine={false} axisLine={false} width={34} />
                    <YAxis yAxisId="impressions" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={48} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                    <Line yAxisId="clicks" type="monotone" dataKey="clicks" stroke="#f97316" strokeWidth={2} dot={false} name="Clicks" />
                    <Line yAxisId="impressions" type="monotone" dataKey="impressions" stroke="#94a3b8" strokeWidth={1} dot={false} name="Impressions" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        ) : (
          <NotConnected title="Search Console" reason={search.reason || "Not connected."} testid="google-search-not-connected" />
        )}
      </Block>

      <Block title="Website traffic" source="Google Analytics 4">
        {data.website?.connected ? (
          <Metric label="Open the detail" value="Website Analytics" onOpen={() => onOpenTab("analytics")} testid="metric-ga4-open" />
        ) : (
          <NotConnected
            title="Google Analytics 4"
            reason={data.website?.reason}
            needs={data.website?.needs}
            links={[
              { href: "https://analytics.google.com/analytics/web/#/admin", label: "Open GA4 admin" },
              { href: "https://console.cloud.google.com/apis/library/analyticsdata.googleapis.com", label: "Enable the Data API" },
            ]}
            where="Measurement is live on the site through Tag Manager; this is only about reading the figures back."
            testid="google-ga4-not-connected"
          />
        )}
      </Block>

      <Block title="Google Ads" source="Google Ads API">
        {data.ads?.connected ? (
          <Metric label="Open the detail" value="Google Ads" onOpen={() => onOpenTab("ads")} testid="metric-ads-open" />
        ) : (
          <NotConnected
            title="Google Ads"
            reason={data.ads?.reason}
            needs={data.ads?.needs}
            links={[
              { href: "https://ads.google.com/aw/apicenter", label: "Request a developer token" },
              { href: "https://ads.google.com/aw/overview", label: "Open Google Ads" },
            ]}
            testid="google-ads-not-connected"
          />
        )}
      </Block>

      <p className="text-[11px] text-slate-400 leading-relaxed">
        Figures are not added across sources. A Search Console click, a GA4 session and an enquiry are measured in
        different ways, so each block is reported against its own source and period. Cost per lead appears once Google
        Ads is connected and its clicks can be reconciled to real enquiries — not as an estimate.
      </p>
    </div>
  );
}
